/**
 * TrainingImpactWizard — DMS-21 (drafts/57).
 * Structured training-impact assessment for a document revision. Replaces the
 * bare "training impact" warning with an explicit 6-question determination:
 *   - any YES  → re-training (Read-&-Understand) is REQUIRED on effective date.
 *   - all  NO  → a documented training WAIVER (no re-training needed).
 * Client-side determination (auditable record + auto-R&U-trigger wiring tracked
 * as a follow-up in drafts/57).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from '@edms/design-system';

const QUESTIONS = [
  { key: 'gmp', def: 'Does the revision change a GMP requirement?' },
  { key: 'process', def: 'Does it change a process step?' },
  { key: 'equipment', def: 'Does it change equipment use or operation?' },
  { key: 'safety', def: 'Does it affect operator or patient safety?' },
  { key: 'quality', def: 'Does it affect product quality?' },
  { key: 'regulatory', def: 'Does it affect a regulatory filing or commitment?' },
] as const;

export function TrainingImpactWizard() {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, boolean | null>>({});

  const answeredCount = QUESTIONS.filter((q) => answers[q.key] != null).length;
  const allAnswered = answeredCount === QUESTIONS.length;
  const anyYes = QUESTIONS.some((q) => answers[q.key] === true);
  const determination: 'required' | 'waiver' | null = allAnswered ? (anyYes ? 'required' : 'waiver') : null;

  function set(key: string, val: boolean) {
    setAnswers((a) => ({ ...a, [key]: val }));
  }

  const toggleBtn = (active: boolean, tone: 'yes' | 'no') => ({
    padding: '4px 14px',
    borderRadius: 999,
    border: `1px solid ${active ? (tone === 'yes' ? '#B91C1C' : '#0E9F7E') : 'var(--color-border-subtle, #d1d5db)'}`,
    background: active ? (tone === 'yes' ? '#FEE2E2' : '#DCFCE7') : 'transparent',
    color: active ? (tone === 'yes' ? '#B91C1C' : '#0E9F7E') : 'var(--color-text-muted, #6B7989)',
    cursor: 'pointer',
    fontWeight: 600 as const,
  });

  return (
    <Card>
      <CardHeader>
        <h2 style={{ margin: 0, fontSize: 16 }}>
          {t('reviewer.trainingImpact.title', { defaultValue: 'Training impact assessment' })}
        </h2>
      </CardHeader>
      <CardBody>
        <p style={{ color: 'var(--color-text-muted, #6B7989)', marginTop: 0 }}>
          {t('reviewer.trainingImpact.help', {
            defaultValue: 'Assess whether this revision needs re-training. Any "Yes" requires Read-&-Understand on the effective date; all "No" supports a documented training waiver.',
          })}
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {QUESTIONS.map((q) => (
            <li key={q.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--color-border-subtle, #f1f5f9)' }}>
              <span>{t(`reviewer.trainingImpact.q.${q.key}`, { defaultValue: q.def })}</span>
              <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button type="button" aria-pressed={answers[q.key] === true} style={toggleBtn(answers[q.key] === true, 'yes')} onClick={() => set(q.key, true)}>
                  {t('common.yes', { defaultValue: 'Yes' })}
                </button>
                <button type="button" aria-pressed={answers[q.key] === false} style={toggleBtn(answers[q.key] === false, 'no')} onClick={() => set(q.key, false)}>
                  {t('common.no', { defaultValue: 'No' })}
                </button>
              </span>
            </li>
          ))}
        </ul>

        <div role="status" aria-live="polite" style={{ marginTop: 14 }}>
          {determination === null && (
            <span style={{ color: 'var(--color-text-muted, #6B7989)' }}>
              {t('reviewer.trainingImpact.pending', { defaultValue: 'Answer all questions to determine training impact.' })} ({answeredCount}/{QUESTIONS.length})
            </span>
          )}
          {determination === 'required' && (
            <span style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 8, padding: '8px 12px', fontWeight: 700, display: 'inline-block' }}>
              ⚠ {t('reviewer.trainingImpact.required', { defaultValue: 'Training REQUIRED — approval will queue Read-&-Understand signatures.' })}
            </span>
          )}
          {determination === 'waiver' && (
            <span style={{ background: '#DCFCE7', color: '#0E9F7E', borderRadius: 8, padding: '8px 12px', fontWeight: 700, display: 'inline-block' }}>
              ✓ {t('reviewer.trainingImpact.waiver', { defaultValue: 'Training WAIVER — no re-training required (documented).' })}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
